import { Form } from '@/common/form/Form';
import { FormFieldGroupContainer } from '@/common/form/FormFieldGroupContainer';
import { useCloneAndRun } from '@/common/hooks/jobs/useCloneAndRun';
import { useModuleFromRoute } from '@/common/hooks/route/useModuleFromRoute';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, FormHelperText, InputLabel, MenuItem, Select, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { UserSelect } from './UserSelect';

export const ApprDialog = ({ data, closeDialog }) => {
  // Dialog
  const [title, setTitle] = useState();
  const [apprRoutes, setApprRoutes] = useState();
  const [apprGroupToUsers, setapprGroupToUsers] = useState();
  const [selectedApprRoute, setSelectedApprRoute] = useState('');
  const module = useModuleFromRoute();

  const rsrc = useRsrc();
  const cloneAndRun = useCloneAndRun();

  const formRef = useRef();

  useEffect(() => {
    const reqParam = {
      entityObjs: data.entityObjs,
      entity: data.entity
    }
    rsrc.data('skdr', 'APPR_ROUTE_DATA_PLUGIN', reqParam, data.entity).then(apprRoutes => {
      // apprRoutes = [];
      setApprRoutes(apprRoutes);
      setTitle(apprRoutes.length === 0 ? 'Approve ' + data.entityDisplayName : 'Route ' + data.entityDisplayName + ' for Approval')
      if (apprRoutes.length === 1) {
        setSelectedApprRoute(apprRoutes[0]);
      }
    })
    rsrc.data('admin', 'GROUP_TO_USERS_DATA_PLUGIN', reqParam, data.entity).then(apprGroupToUsers => {
      setapprGroupToUsers(apprGroupToUsers);
    });
  }, []);

  const submit = () => {
    if (apprRoutes.length === 0) {
      const patchObjs = data.entityObjs.map(entityObj => {
        return { id: entityObj.id, status: 'APPROVED' };
      })
      rsrc.save(module, data.entity, patchObjs).then(() => {
        closeDialog({ apprRoute: false })
      })
      return;
    }

    if (formRef.current.hasErrorState()) {
      formRef.current.touchAll();
      return;
    }

    const entityIds = data.entityObjs.map(entityObj => entityObj.id);
    const jobIdToApprover = formRef.current.formValues;

    cloneAndRun(selectedApprRoute.id, entityIds, jobIdToApprover).then(clonedJob => {
      closeDialog({ apprRoute: true });
    })
  }

  return (
    apprRoutes && apprGroupToUsers &&
    <Dialog onClose={() => closeDialog()} open={true} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {/* If there are no approval routes, the user will simply approve */}
        {apprRoutes.length === 0 &&
          <Typography>Are you sure you want to approve this {data.entityDisplayName}?</Typography>
        }

        {/* If there are approval routes, the user will configure the approval route */}
        {apprRoutes.length > 1 &&
          <>
            <FormControl variant="filled" fullWidth sx={{ marginBottom: '1rem' }}>
              <InputLabel>Approval Route</InputLabel>
              <Select value={selectedApprRoute} onChange={event => setSelectedApprRoute(event.target.value)} data-test-id="appr-selector">
                {apprRoutes.map(apprRoute => <MenuItem key={apprRoute.code} value={apprRoute}>{apprRoute.name}</MenuItem>)}
              </Select>
              <FormHelperText>The Approval Route determines the approval flow of the {data.entityDisplayName}</FormHelperText>
            </FormControl>
            {selectedApprRoute && <Divider sx={{ marginBottom: '1rem' }} />}
          </>
        }

        {selectedApprRoute &&
          <>
            <Typography variant="subtitle1" sx={{ marginBottom: '1rem' }}>Select Approvers</Typography>
            <Form ref={formRef}>
              {selectedApprRoute.steps.map((step, index) =>
                <StepContainer title={'Step ' + (index + 1)} key={selectedApprRoute.code + index}>
                  <FormFieldGroupContainer>
                    {Object.entries(step.apprGroupToJobIds).map(([apprGroup, jobIds]) =>
                      <UserSelect key={apprGroup + index} stepNum={index} apprGroup={apprGroup} jobIds={jobIds} apprGroupToUsers={apprGroupToUsers} />
                    )}
                  </FormFieldGroupContainer>
                </StepContainer>
              )}
            </Form>
          </>
        }
      </DialogContent>
      <DialogActions>
        <Button onClick={() => closeDialog()}>Cancel</Button>
        <Button 
          onClick={() => submit()} data-test-id="appr-submit" 
          disabled={apprRoutes.length > 0 && !selectedApprRoute}>{apprRoutes.length === 0 ? 'Approve' : 'Submit'}</Button>
      </DialogActions>
    </Dialog>
  )
}

const StepContainer = ({ title, children }) => {
  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '16px', // Material 3's softer, rounded corners
        overflow: 'hidden',
        mt: 2,
        boxShadow: 0, // Flat design
        bgcolor: theme => theme.palette.surface.main
      }}
    >
      {title && (
        <Box
          sx={{
            px: 2,
            pt: 1.5,
            // borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1">{title}</Typography>
        </Box>
      )}
      <Box
        sx={{
          px: 2,
          pb: 2,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};
